import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { useChartPeriod } from '@/hooks';
import {
  CurrencyValue,
  Flex,
  formatDatetime,
  formatNumber,
  LineChart
} from '@blockanalitica/ui';
import { ExtendedChartOptions } from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import { useCallback, useMemo, useState } from 'react';

export default function TotalAssetsHistoricGraph() {
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [data, error] = useApi({
    url: `/unichain/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );
  const config = updateChartConfigWithPeriod({
    options: { fill: false } as ExtendedChartOptions
  });

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  const yScaleValueFormatter = useMemo(
    () => (value: number) => {
      return `${formatNumber(value)}`;
    },
    []
  );

  const valueFormatter = useMemo(() => {
    return function valueFormatter(value: string) {
      return (
        <CurrencyValue
          value={value.toString()}
          options={{
            currency: 'USD'
          }}
        />
      );
    };
  }, []);

  const datasets = useMemo(() => {
    return [
      {
        xField: 'date',
        yField: 'circulating_supply'
      }
    ];
  }, []);
  if (error) {
    throw error;
  }
  if (!data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <ChartSkeleton />
      </Flex>
    );
  }
  return (
    <LineChart
      data={data}
      datasets={datasets}
      valueFormatter={valueFormatter}
      yScaleValueFormatter={yScaleValueFormatter}
      timeFilter={timeFilter}
      config={config}
      subValueFormatter={subValueFormatter}
    />
  );
}
